/**
 * «Сравнение моделей» (спека §8): последний готовый монтаж каждой модели (смешанный — отдельной
 * колонкой), до 4 колонок в ряд. Плееры `muted playsinline preload="metadata"` с постером — кадром 0 с
 * первого клипа; «▶ все / ⏸ все / ⟲ в начало» и общий ползунок ставят `currentTime` и
 * `play()`/`pause()` КАЖДОМУ плееру; клик по одному плееру — только он (у каждого свои controls).
 *
 * Под роликом: уровень, подпись состава (смешанный — «Seedance 2.0 + Kling 3.0 Pro (кадры 1–2)»), факт цены (сумма клипов; нет факта у всех — «≈ оценка» курсивом),
 * время монтажа и Σ времени клипов у провайдера, звезда «лучший» (один на проект). Ряд миниатюр по
 * клипам — кадры 1 / 2,5 / 5 с (0 с — постер, не показываем), клик — крупно в `Dialog`; под рядом —
 * чек-лист приёмки текстом: «кружево смылось в тюль» и «появился шлейф» видны на стоп-кадре, а не в
 * движении.
 */

import clsx from "clsx";
import { Pause, Play, RotateCcw, Star } from "lucide-react";
import { useRef, useState } from "react";

import { Btn, Empty, ErrorLine, Hint, Panel } from "@/components/project/ui";
import { Dialog } from "@/components/trends/Dialog";
import { mediaUrl } from "@/lib/media";
import { errorText, usd } from "@/lib/project";
import { usePickCut, type CompareColumn, type Cut } from "@/lib/video";
import { reelText, timingText } from "@/lib/videoCompare";
import { ACCEPTANCE_CHECKLIST, checkFrames, seekTo } from "@/lib/videoRun";


export function ComparePanel({ pid, compare, cuts }: { pid: number; compare: CompareColumn[]; cuts: Cut[] }) {
  const pick = usePickCut(pid);
  const players = useRef(new Map<number, HTMLVideoElement>());
  const [t, setT] = useState(0);
  // пока ползунок тянут, время плеера его не перебивает
  const dragging = useRef(false);
  const [zoom, setZoom] = useState<{ path: string; caption: string } | null>(null);
  const building = cuts.some((c) => c.status === "queued" || c.status === "running");
  const maxS = Math.max(0, ...compare.map((c) => (c.cut.duration_ms ?? 0) / 1000));
  // общий ползунок идёт за первым плеером, у которого есть файл
  const leader = compare.find((c) => c.cut.asset_path)?.cut.id ?? null;

  const each = (fn: (v: HTMLVideoElement) => void) => players.current.forEach(fn);
  const playAll = () => each((v) => void v.play().catch(() => undefined));
  const pauseAll = () => each((v) => v.pause());
  const seekAll = (s: number) => {
    setT(s);
    each((v) => {
      v.currentTime = seekTo(s, v.duration);
    });
  };

  return (
    <Panel title="Сравнение" annot="сравнение моделей">
      {compare.length === 0 ? (
        <Empty>{building ? "Монтаж собирается…" : "Готовых монтажей нет — запустите клипы по моделям."}</Empty>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Btn onClick={playAll} aria-label="Играть все">
              <Play size={13} /> все
            </Btn>
            <Btn onClick={pauseAll} aria-label="Пауза все">
              <Pause size={13} /> все
            </Btn>
            <Btn
              aria-label="В начало"
              onClick={() => {
                pauseAll();
                seekAll(0);
              }}
            >
              <RotateCcw size={13} /> в начало
            </Btn>
            <input
              type="range"
              aria-label="общее время"
              min={0}
              max={maxS || 1}
              step={0.1}
              value={Math.min(t, maxS || 1)}
              onChange={(e) => seekAll(Number(e.target.value))}
              onPointerDown={() => {
                dragging.current = true;
              }}
              onPointerUp={() => {
                dragging.current = false;
              }}
              onPointerCancel={() => {
                dragging.current = false;
              }}
              onBlur={() => {
                dragging.current = false;
              }}
              className="min-w-[200px] flex-1"
            />
            <span className="w-14 text-right text-[12px] whitespace-nowrap text-fg-muted">
              <span className="tnum">
                {t.toLocaleString("ru-RU", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
              </span>{" "}
              с
            </span>
            {building ? <Hint>ещё один монтаж собирается…</Hint> : null}
          </div>

          <div className="grid grid-cols-4 gap-3">
            {compare.map((col) => {
              const c = col.cut;
              const picked = c.pick === "picked";
              return (
                <div
                  key={c.id}
                  className={clsx(
                    "grid min-w-0 content-start gap-1.5 rounded-[var(--radius-ui)] border p-2",
                    picked ? "border-accent ring-1 ring-accent" : "border-border",
                  )}
                  data-annot={`сравнение ${col.label}`}
                >
                  {c.asset_path ? (
                    <video
                      ref={(el) => {
                        if (el) players.current.set(c.id, el);
                        else players.current.delete(c.id);
                      }}
                      src={mediaUrl(c.asset_path)}
                      poster={c.poster_path ? mediaUrl(c.poster_path) : undefined}
                      muted
                      playsInline
                      controls
                      preload="metadata"
                      onTimeUpdate={
                        c.id === leader
                          ? (e) => {
                              if (!dragging.current) setT(e.currentTarget.currentTime);
                            }
                          : undefined
                      }
                      className="aspect-[9/16] w-full rounded-[4px] bg-black object-contain"
                    />
                  ) : null}
                  <div className="flex items-start gap-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="text-[11.5px] text-fg-faint">{col.tier_label ?? (col.model ? "без уровня" : "смешанный")}</div>
                      <div className="text-[13px] leading-snug font-medium text-fg" title={col.model ?? undefined}>
                        {col.label}
                      </div>
                      {c.items.some((it) => it.routed_from) ? (
                        <Hint>смешанный: кадры с лицом — клипы запасной модели</Hint>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className={clsx("grid size-7 place-items-center rounded-full", picked ? "text-accent" : "text-fg-faint hover:text-fg")}
                      title={picked ? "лучший в проекте" : "отметить лучшим (прежняя отметка снимется)"}
                      aria-pressed={picked}
                      disabled={picked || (pick.isPending && pick.variables === c.id)}
                      onClick={() => pick.mutate(c.id)}
                    >
                      <Star size={16} fill={picked ? "currentColor" : "none"} />
                    </button>
                  </div>
                  <div className="text-[12.5px]">
                    <b className="tnum font-semibold text-fg" title="длительность ролика">
                      {reelText(c.duration_ms)}
                    </b>
                    <span className="text-fg-faint">{" · "}</span>
                    {col.cost_is_fact ? (
                      <b className="tnum font-semibold text-fg">{usd(col.cost)}</b>
                    ) : (
                      <i className="tnum text-fg-muted" title="факт есть не у всех клипов">
                        ≈ {usd(col.cost)}
                      </i>
                    )}
                    {c.grade ? <span className="text-fg-faint">{" · "}грейд</span> : null}
                  </div>
                  <div className="text-[11.5px] text-fg-faint" title="сборка — ffmpeg у нас; генерация — сумма времени клипов у провайдера">
                    {timingText(col.render_seconds, col.clip_seconds)}
                  </div>
                  <div className="grid gap-1.5">
                    {c.items.map((it, k) => {
                      const frames = checkFrames(it.frames);
                      return (
                        <div key={`${it.shot_id}-${it.variant_id}`} className="grid gap-0.5">
                          <span className="text-[11px] text-fg-faint">кадр {k + 1}</span>
                          {frames.length === 0 ? (
                            <Hint>миниатюр нет</Hint>
                          ) : (
                            <div className="grid grid-cols-3 gap-1">
                              {frames.map((f) => {
                                const caption = `${col.label} · кадр ${k + 1} · ${(f.ms / 1000).toLocaleString("ru-RU")} с`;
                                return (
                                  <button
                                    key={f.ms}
                                    type="button"
                                    title={caption}
                                    onClick={() => setZoom({ path: f.path, caption })}
                                    className="overflow-hidden rounded-[3px] border border-border hover:border-accent"
                                  >
                                    <img src={mediaUrl(f.path)} alt={caption} loading="lazy" className="aspect-[9/16] w-full object-cover" />
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[12px] text-fg-muted">
            <b className="font-medium text-fg">Чек-лист приёмки:</b> {ACCEPTANCE_CHECKLIST}
          </p>
        </>
      )}
      <ErrorLine text={pick.isError ? errorText(pick.error) : ""} className="mt-2" />

      <Dialog open={zoom !== null} title={zoom?.caption ?? ""} onClose={() => setZoom(null)}>
        {zoom ? (
          <div className="p-4">
            <img src={mediaUrl(zoom.path)} alt={zoom.caption} className="mx-auto max-h-[75vh] w-auto rounded-[var(--radius-ui)]" />
          </div>
        ) : null}
      </Dialog>
    </Panel>
  );
}
